import {
  Badge,
  Button,
  Card,
  Divider,
  Group,
  NumberInput,
  Progress,
  Rating,
  Select,
  Stack,
  Text,
  Title,
} from "@mantine/core";
import { notifications } from "@mantine/notifications";
import { IconStarFilled } from "@tabler/icons-react";
import { useEffect, useState } from "react";
import type { Pricing, Review } from "./types";
import { formatCurrency, formatDate } from "./types";

const MAX_PRICE_CENTS = 99_999_999; // $999,999.99

const CURRENCY_OPTIONS = [
  { label: "USD - US Dollar", value: "usd" },
  { label: "EUR - Euro", value: "eur" },
  { label: "GBP - British Pound", value: "gbp" },
  { label: "CAD - Canadian Dollar", value: "cad" },
  { label: "AUD - Australian Dollar", value: "aud" },
  { label: "JPY - Japanese Yen", value: "jpy" },
  { label: "BRL - Brazilian Real", value: "brl" },
];

interface MonetizationTabProps {
  appId: string;
  pricing: Pricing | null;
  setPricing: (options: {
    variables: {
      input: {
        marketplaceAppId: string;
        type: string;
        priceAmountCents: number;
        currency: string;
        trialDays: number;
      };
    };
  }) => Promise<unknown>;
  refetch: () => Promise<unknown>;
  reviews: Review[];
  totalReviewCount: number;
  averageRating: number | null;
}

export function MonetizationTab({
  appId,
  pricing,
  setPricing,
  refetch,
  reviews,
  totalReviewCount,
  averageRating,
}: MonetizationTabProps) {
  const [pricingType, setPricingType] = useState(pricing?.type ?? "FREE");
  const [priceAmountCents, setPriceAmountCents] = useState(
    pricing?.priceAmountCents ?? 0,
  );
  const [currency, setCurrency] = useState(pricing?.currency ?? "usd");
  const [trialDays, setTrialDays] = useState(pricing?.trialDays ?? 0);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setPricingType(pricing?.type ?? "FREE");
    setPriceAmountCents(pricing?.priceAmountCents ?? 0);
    setCurrency(pricing?.currency ?? "usd");
    setTrialDays(pricing?.trialDays ?? 0);
  }, [pricing]);

  const handleSave = async () => {
    if (pricingType !== "FREE" && priceAmountCents <= 0) {
      notifications.show({
        title: "Validation Error",
        message: "Price must be greater than zero for paid plans.",
        color: "red",
      });
      return;
    }

    setSaving(true);
    try {
      const result = await setPricing({
        variables: {
          input: {
            marketplaceAppId: appId,
            type: pricingType,
            priceAmountCents: pricingType === "FREE" ? 0 : priceAmountCents,
            currency,
            trialDays: pricingType === "SUBSCRIPTION" ? trialDays : 0,
          },
        },
      });

      if ((result as any)?.errors?.length) {
        throw new Error((result as any).errors[0].message);
      }

      notifications.show({
        title: "Pricing Updated",
        message: "App pricing has been saved.",
        color: "green",
        autoClose: 3000,
      });

      refetch();
    } catch (error) {
      notifications.show({
        title: "Error",
        message:
          error instanceof Error ? error.message : "Failed to save pricing",
        color: "red",
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Stack gap="lg">
      {/* Pricing Section */}
      <Title order={5}>Pricing</Title>
      <Text size="sm" c="dimmed">
        Configure how organizations are charged for installing your app.
      </Text>

      {pricing && (
        <Card withBorder padding="md">
          <Group justify="space-between">
            <Stack gap={2}>
              <Text size="sm" fw={500}>
                Current Pricing
              </Text>
              <Group gap="xs">
                <Badge
                  color={pricing.type === "FREE" ? "green" : "blue"}
                  variant="light"
                >
                  {pricing.type}
                </Badge>
                {pricing.type !== "FREE" && (
                  <Text size="sm">
                    {formatCurrency(pricing.priceAmountCents, pricing.currency)}
                    {pricing.type === "SUBSCRIPTION" ? "/month" : " one-time"}
                  </Text>
                )}
                {pricing.trialDays > 0 && (
                  <Badge variant="outline" size="sm">
                    {pricing.trialDays}-day trial
                  </Badge>
                )}
              </Group>
            </Stack>
            <Text size="xs" c="dimmed">
              Updated: {formatDate(pricing.updatedAt)}
            </Text>
          </Group>
        </Card>
      )}

      <Card withBorder padding="lg">
        <Stack gap="md">
          <Select
            label="Pricing Type"
            value={pricingType}
            onChange={(v) => v && setPricingType(v)}
            data={[
              { label: "Free", value: "FREE" },
              { label: "One-Time Purchase", value: "ONE_TIME" },
              { label: "Subscription", value: "SUBSCRIPTION" },
            ]}
          />

          {pricingType !== "FREE" && (
            <>
              <NumberInput
                label="Price (in cents)"
                description="Enter the price in the smallest currency unit (e.g., 999 = $9.99)"
                value={priceAmountCents}
                onChange={(v) =>
                  setPriceAmountCents(typeof v === "number" ? v : 0)
                }
                min={1}
                max={MAX_PRICE_CENTS}
              />

              <Select
                label="Currency"
                description="ISO 4217 currency code"
                value={currency}
                onChange={(v) => v && setCurrency(v)}
                data={CURRENCY_OPTIONS}
              />
            </>
          )}

          {pricingType === "SUBSCRIPTION" && (
            <NumberInput
              label="Trial Days"
              description="Number of free trial days before billing starts"
              value={trialDays}
              onChange={(v) => setTrialDays(typeof v === "number" ? v : 0)}
              min={0}
              max={365}
            />
          )}

          <Button onClick={handleSave} loading={saving}>
            Save Pricing
          </Button>
        </Stack>
      </Card>

      <Divider my="sm" />

      {/* Reviews Section */}
      <Title order={5}>Reviews</Title>

      {averageRating != null && totalReviewCount > 0 && (
        <Card withBorder padding="md">
          <Group gap="lg">
            <Stack gap={2} align="center">
              <Text size="2rem" fw={700}>
                {averageRating.toFixed(1)}
              </Text>
              <Rating value={averageRating} fractions={2} readOnly />
              <Text size="xs" c="dimmed">
                {totalReviewCount} review{totalReviewCount !== 1 ? "s" : ""}
              </Text>
            </Stack>
            <Divider orientation="vertical" />
            <Stack gap={4} style={{ flex: 1 }}>
              {[5, 4, 3, 2, 1].map((star) => {
                const count = reviews.filter((r) => r.rating === star).length;
                const percent =
                  reviews.length > 0 ? (count / reviews.length) * 100 : 0;
                return (
                  <Group key={star} gap="xs">
                    <Group gap={2} style={{ width: 60 }}>
                      <Text size="xs">{star}</Text>
                      <IconStarFilled size={12} color="#fab005" />
                    </Group>
                    <Progress
                      value={percent}
                      size="sm"
                      style={{ flex: 1 }}
                      color="yellow"
                    />
                    <Text size="xs" c="dimmed" style={{ width: 24 }}>
                      {count}
                    </Text>
                  </Group>
                );
              })}
              {totalReviewCount > reviews.length && (
                <Text size="xs" c="dimmed" mt={4}>
                  Distribution based on most recent {reviews.length} reviews
                </Text>
              )}
            </Stack>
          </Group>
        </Card>
      )}

      {reviews.length === 0 && (
        <Card withBorder padding="xl">
          <Text c="dimmed" ta="center" py="md">
            No reviews yet.
          </Text>
        </Card>
      )}

      {reviews.map((review) => (
        <Card key={review.id} withBorder padding="md">
          <Group justify="space-between" mb="xs">
            <Group gap="xs">
              <Rating value={review.rating} readOnly size="xs" />
              <Text size="sm" fw={600}>
                {review.title}
              </Text>
            </Group>
            <Text size="xs" c="dimmed">
              {formatDate(review.createdAt)}
            </Text>
          </Group>
          <Text size="sm">{review.body}</Text>
        </Card>
      ))}

      {totalReviewCount > reviews.length && (
        <Text size="sm" c="dimmed" ta="center">
          Showing {reviews.length} of {totalReviewCount} reviews
        </Text>
      )}
    </Stack>
  );
}
